
import { useState, useEffect } from "react";
import { StudentLayout } from "@/components/student/layout";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useEnrollments } from "@/hooks/useEnrollments";
import EmptyDashboardState from "@/components/student/EmptyDashboardState";
import ContinueLearningCard from "@/components/student/ContinueLearningCard";
import EnrolledCoursesList from "@/components/student/EnrolledCoursesList";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { useCourseProgress } from "@/hooks/useCourseProgress";
import { useLessonCompletions } from "@/hooks/useLessonCompletions";
import { useAuth } from "@/providers/AuthProvider";
import { devLog } from "@/utils/devLog";
import { Enrollment } from "@/types/enrollment";
import { calculateCourseProgress } from "@/hooks/useLessonCompletions";
import { useNavigate, useLocation } from "react-router-dom";

const StudentDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { enrollments, isLoading: enrollmentsLoading, error: enrollmentsError } = useEnrollments(user?.id);
  const [userData, setUserData] = useState<{id: string; fullName: string} | null>(null);
  const { courseProgress, loading: progressLoading } = useCourseProgress();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if we're on the My Learning route to adjust UI accordingly
  const isMyLearningRoute = location.pathname.includes('/my-learning');
  
  // Log when user ID is available
  useEffect(() => {
    if (user?.id) {
      devLog("[StudentDashboard] User ID available:", user.id);
    } else {
      devLog("[StudentDashboard] No user ID available yet");
    }
  }, [user]);
  
  // Log when enrollments data changes
  useEffect(() => {
    devLog("[StudentDashboard] Enrollments updated:", { 
      count: (enrollments as Enrollment[])?.length || 0,
      isLoading: enrollmentsLoading,
      error: enrollmentsError ? "Error fetching enrollments" : null
    });
  }, [enrollments, enrollmentsLoading, enrollmentsError]);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        devLog("[StudentDashboard] Fetching user data");
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          devLog("[StudentDashboard] User found, fetching profile data");
          const { data: profileData, error } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', user.id)
            .single();
            
          if (error) {
            devLog("[StudentDashboard] Error fetching profile:", error);
            throw error;
          }
          
          setUserData({ id: user.id, fullName: profileData?.full_name || 'Student' });
          devLog("[StudentDashboard] User data set:", { 
            id: user.id, 
            fullName: profileData?.full_name || 'Student' 
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Could not load your profile information");
      } finally {
        setIsLoading(false);
        devLog("[StudentDashboard] Finished loading user data");
      }
    };
    
    fetchUserData();
  }, []);

  // Get the most recently updated course progress
  const getInProgressCourse = () => {
    if (!courseProgress || courseProgress.length === 0) return null;
    return courseProgress[0]; // Already sorted by updated_at desc
  };
  
  const inProgressCourse = getInProgressCourse();

  // Calculate real progress for the in-progress course
  const [inProgressPercent, setInProgressPercent] = useState(0);

  useEffect(() => {
    const fetchProgress = async () => {
      if (inProgressCourse && user?.id) {
        try {
          const progress = await calculateCourseProgress(
            inProgressCourse.course_id,
            user.id
          );
          setInProgressPercent(progress);
          devLog("[StudentDashboard] Course progress calculated:", { 
            courseId: inProgressCourse.course_id, 
            progress 
          });
        } catch (error) {
          console.error("Error calculating progress:", error);
          setInProgressPercent(0);
        }
      }
    };

    fetchProgress();
  }, [inProgressCourse, user]);

  if (isLoading || enrollmentsLoading || progressLoading) {
    return (
      <StudentLayout
        title={isMyLearningRoute ? "My Learning" : `Welcome back, ${userData?.fullName || 'Student'}!`}
        description={isMyLearningRoute ? "Continue where you left off" : "Here's an overview of your learning progress"}
      >
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout 
      title={isMyLearningRoute ? "My Learning" : `Welcome back, ${userData?.fullName || 'Student'}!`}
      description={isMyLearningRoute ? "Continue where you left off" : "Here's an overview of your learning progress"}
    >
      {enrollmentsError && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-700">Error loading your courses. Please try refreshing the page.</p>
          </CardContent>
        </Card>
      )}
      
      {!enrollmentsError && (enrollments as Enrollment[]).length === 0 ? (
        <EmptyDashboardState />
      ) : (
        <>
          {/* Continue Learning Section - only show on dashboard, not My Learning */}
          {!isMyLearningRoute && inProgressCourse && inProgressCourse.courses && (
            <ContinueLearningCard 
              course={{
                id: inProgressCourse.course_id,
                title: inProgressCourse.courses.title,
                thumbnail_url: inProgressCourse.courses.thumbnail_url,
                profiles: null // We could fetch this if needed
              }}
              progress={inProgressPercent}
              lastLesson={inProgressCourse.lessons?.title || "Unknown lesson"}
              lessonId={inProgressCourse.last_lesson_id}
            />
          )}
          
          {/* Enrolled Courses Section */}
          <EnrolledCoursesList 
            enrollments={enrollments as Enrollment[]}
            isLoading={false}
          />
        </>
      )}
    </StudentLayout>
  );
};

export default StudentDashboard;
